<template>
  <v-container>
    <Heading msg="Recent Schedules" />

    <v-snackbar
      transition="slide-y-transition"
      v-model="snackbar"
      top
      timeout="6000"
      color="sokoDeep"
      dark
      >{{ snackbarMsg }}</v-snackbar
    >

    <!-- search -->
    <v-row>
      <v-col cols="11" sm="6" class="mx-auto"
        ><v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Search"
          single-line
          hide-details
          rounded
          outlined
          color="#000"
          dense
        ></v-text-field
      ></v-col>
    </v-row>
    <!-- end search -->

    <!-- data table -->
    <v-data-table
      :headers="headers"
      :items="allOrders"
      :search="search"
      :items-per-page="5"
      :expanded.sync="expanded"
      single-expand
      item-key="orderid"
      show-expand
      :loading="loading"
      loading-text="Loading... Please wait"
      class="mt-5 mb-12 elevation-1"
    >
      <!-- loading progressbar -->
      <template slot="progress">
        <v-progress-linear
          color="sokoPrimary"
          indeterminate
        ></v-progress-linear>
      </template>
      <!-- end loading progressbar -->

      <!-- customise table content -->
      <!-- orderid -->
      <template v-slot:item.orderid="{ item }">
        #{{ item.orderid.slice(-5) }}
      </template>

      <!-- deliverydate -->
      <template v-slot:item.deliverydate="{ item }">
        {{ momentize(item.deliverydate.slice(0, 10)) }}
      </template>

      <!-- orderstatus -->
      <template v-slot:item.orderstatus="{ item }">
        <span
          class="font-weight-bold"
          :style="`color:${getFeature(item.orderstatus, 'color')}`"
          >{{ item.orderstatus }}</span
        >
      </template>

      <!-- expanded -->
      <template v-slot:expanded-item="{ headers, item }">
        <td :colspan="headers.length">
          <div class="px-2 py-4" style="border-bottom: 1px solid #f55e15">
            <v-row>
              <!-- order-overview -->
              <v-col cols="12" sm="7">
                <ul class="mb-3">
                  <li>order {{ item.orderid }}</li>
                  <li class="font-weight-bold">
                    <span class="sokoDeep--text">
                      {{ item.itemtype }}
                    </span>
                    <span class="grey--text"> ({{ item.vehicletype }})</span>
                  </li>
                </ul>

                <h4 class="grey--text">From</h4>
                <ul>
                  <li>
                    <v-icon v-bind="iconProps">mdi-account-circle</v-icon
                    >{{ item.sendername }} ({{ item.senderphone }})
                  </li>
                  <li>
                    <v-icon v-bind="iconProps">mdi-map-marker</v-icon
                    >{{ item.pickup_location }}
                  </li>
                </ul>

                <h4 class="grey--text">To</h4>
                <ul>
                  <li>
                    <v-icon v-bind="iconProps"
                      >mdi-account-circle-outline</v-icon
                    >{{ item.recepientname }} ({{ item.recepientphone }})
                  </li>
                  <li>
                    <v-icon v-bind="iconProps">mdi-map-marker-radius</v-icon
                    >{{ item.dropoff_location }}
                  </li>
                </ul>
              </v-col>

              <!-- progress, actions and info -->
              <v-col cols="12" sm="5" class="text-center">
                <!-- feedback form -->
                <v-card tile flat v-if="item.orderstatus === 'completed'">
                  <h3>Give Feedback</h3>
                  <span class="grey--text">your feedback makes us better.</span>
                  <v-form class="mt-2">
                    <v-rating
                      v-model="feedback.rating"
                      color="sokoDeep"
                      background-color="grey darken-1"
                      empty-icon="$ratingFull"
                      half-increments
                      hover
                    ></v-rating>
                    <v-text-field
                      label="summary"
                      outlined
                      rounded
                      color="black"
                      dense
                      v-model="feedback.summary"
                    ></v-text-field>
                    <v-btn block class="sokoDeep" dark rounded depressed
                      >submit feedback</v-btn
                    >
                  </v-form>
                </v-card>
                <!-- progress -->
                <v-progress-circular
                  v-else
                  :value="getFeature(item.orderstatus, 'value')"
                  :size="100"
                  :width="20"
                  striped
                  :color="getFeature(item.orderstatus, 'color')"
                  class="my-3"
                >
                  <v-icon :color="getFeature(item.orderstatus, 'color')">{{
                    getFeature(item.orderstatus, "icon")
                  }}</v-icon>
                </v-progress-circular>
                <!-- handle statuses with actions and infos -->
                <template
                  v-if="['declined', 'pending'].includes(item.orderstatus)"
                >
                  <v-btn
                    :color="getFeature(item.orderstatus, 'color')"
                    dark
                    @click="makePayment(item)"
                    :loading="payLoading"
                    depressed
                    rounded
                    class="mx-auto d-block my-5"
                    >make payment</v-btn
                  >
                </template>

                <template
                  v-else-if="
                    ['processed', 'en route'].includes(item.orderstatus)
                  "
                >
                  <span class="sokoDeep--text my-5 text-center d-block">
                    <template v-if="item.orderstatus === 'processed'"
                      ><v-icon class="sokoDeep--text mr-2"
                        >mdi-information-outline</v-icon
                      >If you are not contacted in 15 minutes, please call the
                      SokoDelivery hotline on {{ sokoHotline }}</template
                    >
                    <template v-else>We are working on it.</template>
                  </span>
                </template>
              </v-col>
            </v-row>
          </div>
        </td>
      </template>
    </v-data-table>
    <!-- end data table -->

    <!-- waiting dialog -->
    <v-dialog v-model="paymentDialog" max-width="290">
      <v-card class="fill-height pa-10" fluid flat>
        <v-list>
          <v-list-item>
            <v-list-item-content><WaitAnimation /> </v-list-item-content>
          </v-list-item>
          <v-list-item class="text-center mt-3"
            >Please wait while we process your transaction.</v-list-item
          >
        </v-list>
      </v-card>
    </v-dialog>
    <!-- end waiting dialog -->
  </v-container>
</template>

<script>
import { mapActions } from "vuex";
import moment from "moment";
import { getFeature } from "@/externals/styles/Features";
import WaitAnimation from "@/components/Layout/WaitAnimation.vue";
import Heading from "@/components/Layout/Heading.vue";
import {
  calcDistance,
  calcFee,
  initTransact,
  initCheckout,
} from "@/externals/utils/Scheduler.js";

export default {
  name: "Summary",

  components: { WaitAnimation, Heading },

  data: () => ({
    search: "",
    loading: true,
    payLoading: false,

    expanded: [], // containt object of the expanded item
    sokoHotline: "+2330301234567",

    snackbar: false,
    snackbarMsg: "Error!",

    paymentDialog: false,

    // feedback
    feedback: {
      rating: 5,
      summary: "",
    },

    // props
    iconProps: {
      class: "mr-1 sokoDeep--text",
      style: "font-size: 0.9rem;",
    },

    headers: [
      {
        text: "ORDER",
        align: "start",
        filterable: true,
        sortable: false,
        value: "orderid",
      },
      {
        text: "DATE",
        filterable: true,
        value: "deliverydate",
      },
      {
        text: "RECIPIENT",
        filterable: true,
        value: "recepientname",
      },
      {
        text: "STATUS",
        filterable: true,
        value: "orderstatus",
      },
      { text: "", value: "data-table-expand" },
    ],
  }),

  methods: {
    ...mapActions(["makeFetch"]),

    getFeature,

    momentize(d, str) {
      if (str === "datetime")
        return moment(d).format("MMMM Do, YYYY, h:mm:ss a");
      else return moment(d).format("MMMM Do, YYYY");
    },

    async fetchOrders() {
      await this.makeFetch({
        method: "GET",
        withId: true,
        addUrl: "/get-orders",
        stateVar: "orders",
      }).catch((err) => {
        console.log(err);
        return false;
      });
    },

    async makePayment(order) {
      this.payLoading = true;

      // calculate distance
      const distanceInfo = await calcDistance(
        order.pickup_location,
        order.dropoff_location
      );

      if (!distanceInfo) {
        this.showSnackbar();
        return;
      }

      const paymentInfo = await calcFee(
        distanceInfo,
        order.vehicletype,
        order.pickup_location
      );

      if (!paymentInfo) {
        this.showSnackbar(
          "Sorry, we could not find a rider for your delivery."
        );
        return;
      }

      // get transaction id
      const transaction = await initTransact(paymentInfo, order);

      if (!transaction) {
        this.showSnackbar("Could not create a transaction for your payment.");
        return;
      }

      // make payment
      initCheckout(transaction, paymentInfo, order);

      this.showSnackbar("Redirecting to checkout page...", true);
    },

    showSnackbar(msg, isok) {
      this.snackbar = true;
      if (!msg) {
        msg = "An error occurred while trying to make payment.";
      }
      if (!isok) msg += " Please try again later!";
      this.snackbarMsg = msg;
      this.payLoading = isok;
    },
  },

  computed: {
    allOrders() {
      return (
        this.$store.getters
          .getCollection("orders")
          .filter((order) => order.orderid) || []
      );
    },
  },

  watch: {
    allOrders() {
      const src = this.$route.query.src;

      if (
        this.allOrders[0] !== undefined &&
        src === "skp" &&
        this.allOrders[0].orderstatus === "pending"
      )
        this.paymentDialog = true;
      else this.paymentDialog = false;
    },
  },

  async mounted() {
    await this.fetchOrders();

    this.expanded.push(this.allOrders[0]);

    // console.log(this.allOrders);

    this.loading = false;

    /* uncomment when using ExpressPay
    
    const oid = this.$route.query["order-id"];
    const tkn = this.$route.query.token;

    if (tkn) {
      // remind for payment checking
      this.makeFetch({
        method: "post", //post method
        goTo: "auth",
        body: { token: tkn, "order-id": oid },
        addUrl: "/paymentcb", //endpoint route
      }).catch((err) => console.error(err));
    }
    */
  },
};
</script>

<style scoped></style>
